import {keycloakService} from '@ndw/react-keycloak-authentication';
import {subSeconds} from 'date-fns/subSeconds';
import {jwtDecode} from 'jwt-decode';
import {LayerSpecification} from 'maplibre-gl';
import {combineLatest, firstValueFrom, from, mergeMap, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {BACKEND_V2_BASE_URL, DATA_STREAMER_BASE_URL, IS_DEV} from '../../applicationContext';
import {FrontOfficeEventStatus} from '../../constants';
import {ApiError, DataStreamerApi} from '../../generated/DataStreamerApi';
import {
    DataSourceConfig,
    SearchOptionDTO,
    UpdateIncidentDTO,
    ViewerApiClientV2
} from '../../generated/ViewerApiV2Client';
import {AccessDTO} from '../../interfaces/AccessDTO';
import {DATA_SOURCE_STATUS_DEACTIVATED, DataSource, DataSourceId} from '../../interfaces/DataSource';
import {JwtToken} from '../../interfaces/jwt-token';
import {LegendDTO} from '../../interfaces/Legend';
import {MapLayer} from '../../interfaces/MapLayer';
import {VersionMetadata} from '../../interfaces/Metadata';
import {PrefixFilterOptions} from '../../interfaces/PrefixFilterOptions';
import {SearchFeature} from '../../interfaces/SearchFeature';
import {WAZE_ALERT_ITEM_SUB_TYPES} from '../../interfaces/WazeAlertItemStatus';
import {
    AccessDataActionTypes,
    viewerApiServiceReceivedAccessData,
    viewerApiServiceReceivingAccessDataFailed,
} from './actions/accessData';
import {
    HistoricalDataDataActionTypes,
    HistoryPayload,
    viewerApiServiceReceivedHistoricalData,
    viewerApiServiceReceivingHistoricalDataFailedWithUnauthorizedError,
    viewerApiServiceReceivingHistoricalDataFailedWithUnexpectedError,
} from './actions/historicalDataData';
import {
    LayerConfigurationDataActionTypes,
    viewerApiServiceReceivedLayerConfiguration,
    viewerApiServiceReceivingLayerConfigurationUnexpectedError,
} from './actions/layerConfigurationData';
import {
    LegendDataActionTypes,
    viewerApiServiceReceivedLegendDate,
    viewerApiServiceReceivingLegendDataUnexpectedError,
} from './actions/legendData';
import {
    LoadBridgeHistoryActionTypes,
    viewerApiServiceReceivedBridgeHistory,
    viewerApiServiceReceivingBridgeHistoryFailedWithUnauthorizedError,
    viewerApiServiceReceivingBridgeHistoryFailedWithUnexpectedError,
} from './actions/loadBridgeHistory';
import {
    viewApiServiceLoadPrefixV2DataCompleted,
    ViewerApiServiceLoadPrefixData,
    viewerApiServiceLoadPrefixDataFailedWithUnexpectedError,
} from './actions/loadPrefixData';
import {
    NotificationPaneActionTypes,
    viewerApiServiceReceivedNotificationMessage,
    viewerApiServiceReceivingNotificationMessageFailedWithNotFoundError,
    viewerApiServiceReceivingNotificationMessageFailedWithUnauthorizedError,
    viewerApiServiceReceivingNotificationMessageFailedWithUnexpectedError,
    viewerApiServiceSavedNotificationMessage,
    viewerApiServiceSavingNotificationFailedWithUnauthorizedException,
    viewerApiServiceSavingNotificationFailedWithUnexpectedError,
    viewerApiServiceSavingNotificationMessageFailedWithBadRequestException,
} from './actions/notificationPane';
import {
    ViewerApiServiceSearchOptions,
    viewerApiServiceSearchOptionsCompleted,
    viewerApiServiceSearchOptionsFailedWithUnexpectedError,
} from './actions/searchOptions';
import {
    handleMetadataReceivedForDataSource,
    noMetadataHandlerForDataSourceFound,
    SourceDataDataActionTypes,
    viewerApiServiceReceivedSourceData,
    viewerApiServiceReceivedSourceDataWithNoUpdate,
    viewerApiServiceReceivingSourceDataFailedWithBadRequest,
    viewerApiServiceReceivingSourceDataFailedWithNotFoundError,
    viewerApiServiceReceivingSourceDataFailedWithUnauthorizedError,
    viewerApiServiceReceivingSourceDataUnexpectedError,
} from './actions/sourceDataData';
import {
    viewerApiServiceUpdatedWazeOpen,
    viewerApiServiceUpdatingWazeOpenFailedWithBadRequestExceptionError,
    viewerApiServiceUpdatingWazeOpenFailedWithUnauthorizedExceptionError,
    viewerApiServiceUpdatingWazeOpenFailedWithUnexpectedError,
    WazeOpenActionTypes,
} from './actions/wazeOpen';
import {
    viewerApiServiceReceivedWazeStatusCloseSuccessful,
    viewerApiServiceReceivingWazeStatusCloseFailedWithBadRequestResponse,
    viewerApiServiceReceivingWazeStatusCloseFailedWithUnauthorizedError,
    viewerApiServiceReceivingWazeStatusCloseFailedWithUnexpectedError,
    WazeStatusCloseDataActionTypes,
} from './actions/wazeStatusCloseData';
import {
    viewerApiServiceSuccessFullyUpdatedWazeStatusData,
    viewerApiServiceUpdatingWazeStatusFailedWithUnauthorizedError,
    viewerApiServiceUpdatingWazeStatusUnexpectedError,
    WazeStatusDataActionTypes,
} from './actions/wazeStatusData';

export const HTTP_STATUS = {
    OK: 200,
    NO_CONTENT: 204,
    NOT_MODIFIED: 304,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    INTERNAL_SERVER_ERROR: 500
};

const viewerApiClientV2 = async (): Promise<ViewerApiClientV2> => {
    const token = await firstValueFrom(keycloakService.token());
    return new ViewerApiClientV2({
        BASE: BACKEND_V2_BASE_URL ?? undefined,
        TOKEN: token,
    });
};

const dataStreamerApi = async (): Promise<DataStreamerApi> => {
    const token = await firstValueFrom(keycloakService.token());
    return new DataStreamerApi({
        BASE: DATA_STREAMER_BASE_URL ?? undefined,
        TOKEN: token,
    });
};

export const loadLayerConfiguration = (): Observable<LayerConfigurationDataActionTypes> => {
    return combineLatest([
        from(viewerApiClientV2().then((api) => api.mapLayerController.getMapLayers())),
        from(viewerApiClientV2().then((api) => api.dataSourceController.getDataSources())),
    ]).pipe(
        map(([mapLayerConfigs, dataSourceConfigs]) => {
            const maplayerDtos: MapLayer[] = mapLayerConfigs.map((mapLayerConfig) => ({
                id: mapLayerConfig.id,
                name: mapLayerConfig.name,
                category: mapLayerConfig.category,
                description: mapLayerConfig.description,
                explanation: mapLayerConfig.explanation || null,
                source: mapLayerConfig.source,
                layers: mapLayerConfig.layers as LayerSpecification[],
                zIndex: mapLayerConfig.zindex || 0,
                isActive: false,
            }));

            const datasources: DataSource[] = dataSourceConfigs.map((dataSourceConfig) => {
                return {
                    id: dataSourceConfig.id,
                    name: dataSourceConfig.name,
                    loadBaseData: dataSourceConfig.loadBaseData || false,
                    refreshUpdate: dataSourceConfig.refreshUpdate || false,
                    dataLoadMethod:
                        dataSourceConfig.dataLoadMethod === DataSourceConfig.dataLoadMethod.GEO_JSON
                            ? 'GeoJSON'
                            : 'VectorTiles',
                    updateInterval: dataSourceConfig.updateInterval || 60000,
                    sourceLayer: dataSourceConfig.sourceLayer || '',
                    sourceConfiguration: dataSourceConfig.sourceConfiguration,
                    searchProperty: dataSourceConfig.searchProperty,
                    websocketReload: dataSourceConfig.websocketReload || false,
                    websocketUrl: dataSourceConfig.websocketUrl || '',
                    colorBlindSupported: dataSourceConfig.colorBlindSupported || false,
                    streamable: dataSourceConfig.streamable,
                    currentIdentifier: null,
                    isActive: false,
                    lastUpdateReceived: null,
                    layerFilteredMapData: null,
                    layerMapData: null,
                    searchFeatures: [],
                    sourceUpdateIdentifier: null,
                    status: DATA_SOURCE_STATUS_DEACTIVATED,
                    lastSnapshotUpdateTimestamp: null,
                    dataStreamerId: dataSourceConfig.dataStreamerId,
                    searchOptionSource: dataSourceConfig.searchOptionSource,
                } as unknown as DataSource;
            });
            return viewerApiServiceReceivedLayerConfiguration(maplayerDtos, datasources);
        }),
        catchError(() => of(viewerApiServiceReceivingLayerConfigurationUnexpectedError())),
    );
};

export const loadSourceData = (
    dataSource: DataSource,
    layerType: string,
): Observable<SourceDataDataActionTypes> => {
    const getDataPromise = dataStreamerApi()
        .then((api) => api.snapshotController.getSnapshotById(dataSource.dataStreamerId));

    return from(getDataPromise).pipe(
        map((response) => viewerApiServiceReceivedSourceData(response, dataSource, layerType)),
        catchError((apiError: ApiError) => {
            switch (apiError.status) {
                case HTTP_STATUS.NOT_MODIFIED:
                    return of(viewerApiServiceReceivedSourceDataWithNoUpdate());
                case HTTP_STATUS.BAD_REQUEST:
                    return of(viewerApiServiceReceivingSourceDataFailedWithBadRequest());
                case HTTP_STATUS.FORBIDDEN:
                    return of(viewerApiServiceReceivingSourceDataFailedWithUnauthorizedError(
                        dataSource.id,
                        dataSource.name,
                        layerType,
                    ));
                case HTTP_STATUS.NOT_FOUND:
                    return of(viewerApiServiceReceivingSourceDataFailedWithNotFoundError(
                        dataSource.id,
                        dataSource.name,
                        layerType,
                    ));
                case HTTP_STATUS.INTERNAL_SERVER_ERROR:
                default:
                    return of(viewerApiServiceReceivingSourceDataUnexpectedError(
                        apiError.status,
                        dataSource.id,
                        dataSource.name,
                        layerType,
                    ));
            }
        }),
        catchError(() =>
            of(viewerApiServiceReceivingSourceDataUnexpectedError(-1, dataSource.id, dataSource.name, layerType)),
        ),
    );
};

export const loadHistoricalData = (
    dataType: 'avg' | 'travel-time-v2',
    mapSourceID: string,
    featureID: number | string,
    locationID: string,
    timestamp: number,
): Observable<HistoricalDataDataActionTypes> => {
    const measurementStartDate = subSeconds(new Date(), timestamp);

    let promise;
    if (dataType === 'travel-time-v2') {
        promise = viewerApiClientV2().then((api) =>
            api.travelTimeProxyController.getMeasurementHistory(locationID, measurementStartDate.toISOString()))
            .then(response => ({status: 200, body: response}));
    } else {
        promise = viewerApiClientV2().then(api => api.avgProxyController.getMeasurementHistory1(locationID, measurementStartDate.toISOString()))
            .then(response => ({status: 200, body: response}));
    }

    return from(promise).pipe(
        map((response) => {
            switch (response.status) {
                case HTTP_STATUS.OK:
                    return viewerApiServiceReceivedHistoricalData({
                        dataType: dataType,
                        mapSourceID: mapSourceID,
                        featureID: featureID,
                        locationID: locationID,
                        timestamp: timestamp,
                        data: response.body,
                    } as HistoryPayload);
                case HTTP_STATUS.FORBIDDEN:
                    return viewerApiServiceReceivingHistoricalDataFailedWithUnauthorizedError();
                case HTTP_STATUS.INTERNAL_SERVER_ERROR:
                case HTTP_STATUS.BAD_REQUEST:
                default:
                    return viewerApiServiceReceivingHistoricalDataFailedWithUnexpectedError();
            }
        }),
        catchError(() => of(viewerApiServiceReceivingHistoricalDataFailedWithUnexpectedError())),
    );
};

export const loadBridgeHistory = (isrsCode: string): Observable<LoadBridgeHistoryActionTypes> => {
    const promise = viewerApiClientV2().then((api) =>
        api.bridgeProxyController.getBridgeHistory(isrsCode),
    );

    return from(promise).pipe(
        map((response) => viewerApiServiceReceivedBridgeHistory(isrsCode, response)),
        catchError((apiError: ApiError) => {
            switch (apiError.status) {
                case HTTP_STATUS.FORBIDDEN:
                    return of(viewerApiServiceReceivingBridgeHistoryFailedWithUnauthorizedError());
                default:
                    return of(viewerApiServiceReceivingBridgeHistoryFailedWithUnexpectedError());
            }
        })
    );
};

export const setWazeOpen = (id: string): Observable<WazeOpenActionTypes> => {
    const promise = viewerApiClientV2().then((api) =>
        api.wazeIncidentsProxyController.lockIncident(id),
    );

    return from(promise).pipe(
        map(() => viewerApiServiceUpdatedWazeOpen()),
        catchError((apiError: ApiError) => {
            switch (apiError.status) {
                case HTTP_STATUS.BAD_REQUEST:
                    return of(viewerApiServiceUpdatingWazeOpenFailedWithBadRequestExceptionError());
                case HTTP_STATUS.FORBIDDEN:
                    return of(viewerApiServiceUpdatingWazeOpenFailedWithUnauthorizedExceptionError());
                case HTTP_STATUS.INTERNAL_SERVER_ERROR:
                default:
                    return of(viewerApiServiceUpdatingWazeOpenFailedWithUnexpectedError());
            }
        }),
    );
};

export const updateWazeStatus = (
    id: string,
    name: string,
    status?: FrontOfficeEventStatus,
    hectometrePost?: string,
    subType?: WAZE_ALERT_ITEM_SUB_TYPES,
): Observable<WazeStatusDataActionTypes> => {
    const promise = viewerApiClientV2().then((api) =>
        api.wazeIncidentsProxyController.updateIncident(id, {
            status: frontOfficeEventStatusToStatusDTO(status),
            overriddenRoadInformation: hectometrePost,
            overriddenType: subType
        })
    );

    return from(promise).pipe(
        map(() => viewerApiServiceSuccessFullyUpdatedWazeStatusData(id, name)),
        catchError((apiError: ApiError) => {
            if (apiError.status === HTTP_STATUS.FORBIDDEN) {
                return of(viewerApiServiceUpdatingWazeStatusFailedWithUnauthorizedError(id, name));
            } else {
                return of(viewerApiServiceUpdatingWazeStatusUnexpectedError(id, name));
            }
        }),
    );
};

const frontOfficeEventStatusToStatusDTO = (foStatus?: FrontOfficeEventStatus) => {
    switch(foStatus) {
        case FrontOfficeEventStatus.none:
            return UpdateIncidentDTO.status.NOT_REVIEWED;
        case  FrontOfficeEventStatus.knowMessage:
            return UpdateIncidentDTO.status.KNOW_MESSAGE;
        case FrontOfficeEventStatus.noAction:
            return UpdateIncidentDTO.status.NO_ACTION;
        case FrontOfficeEventStatus.selfAction:
            return UpdateIncidentDTO.status.SELF_ACTION;
        case FrontOfficeEventStatus.startAction:
            return UpdateIncidentDTO.status.START_ACTION;
        default:
            return undefined;
    }
};

export const updateWazeStatusClose = (id: string): Observable<WazeStatusCloseDataActionTypes> => {
    const promise = viewerApiClientV2().then((api) =>
        api.wazeIncidentsProxyController.unlockIncident(id),
    );

    return from(promise).pipe(
        map(() => viewerApiServiceReceivedWazeStatusCloseSuccessful(id)),
        catchError((apiError: ApiError) => {
            switch (apiError.status) {
                case HTTP_STATUS.BAD_REQUEST:
                    return of(viewerApiServiceReceivingWazeStatusCloseFailedWithBadRequestResponse());
                case HTTP_STATUS.FORBIDDEN:
                    return of(viewerApiServiceReceivingWazeStatusCloseFailedWithUnauthorizedError());
                case HTTP_STATUS.INTERNAL_SERVER_ERROR:
                default:
                    return of(viewerApiServiceReceivingWazeStatusCloseFailedWithUnexpectedError());
            }
        }),
    );
};

export const loadLegendData = (): Observable<LegendDataActionTypes> => {
    const request = '/assets/legend.json';
    return from(fetch(request))
        .pipe(
            mergeMap(response => response.json() as Promise<LegendDTO>),
            map((response) => viewerApiServiceReceivedLegendDate(response)),
            catchError(() => of(viewerApiServiceReceivingLegendDataUnexpectedError())),
        );
};

export const updateNotificationMessage = (message: string): Observable<NotificationPaneActionTypes> => {
    const promise = message.length === 0 ?
        viewerApiClientV2().then(api => api.notificationController.deleteNotification())
        : viewerApiClientV2().then((api) =>
            api.notificationController.updateNotification({
                message,
            }),
        );

    return from(promise).pipe(
        map(() => viewerApiServiceSavedNotificationMessage()),
        catchError((apiError: ApiError) => {
            switch (apiError.status) {
                case HTTP_STATUS.BAD_REQUEST:
                    return of(viewerApiServiceSavingNotificationMessageFailedWithBadRequestException());
                case HTTP_STATUS.UNAUTHORIZED:
                case HTTP_STATUS.FORBIDDEN:
                    return of(viewerApiServiceSavingNotificationFailedWithUnauthorizedException());
                default:
                    return of(viewerApiServiceSavingNotificationFailedWithUnexpectedError());

            }
        }),
    );
};

export const loadNotificationData = (): Observable<NotificationPaneActionTypes> => {
    const promise = viewerApiClientV2().then((api) => api.notificationController.getActiveNotification());

    return from(promise).pipe(
        map((notification) => viewerApiServiceReceivedNotificationMessage(notification)),
        catchError((response: ApiError) => {
            switch (response.status) {
                case HTTP_STATUS.FORBIDDEN:
                    return of(viewerApiServiceReceivingNotificationMessageFailedWithUnauthorizedError());
                case HTTP_STATUS.NOT_FOUND:
                    return of(viewerApiServiceReceivingNotificationMessageFailedWithNotFoundError());
                case HTTP_STATUS.INTERNAL_SERVER_ERROR:
                default:
                    return of(viewerApiServiceReceivingNotificationMessageFailedWithUnexpectedError());
            }
        }),
    );
};

export const loadAccessData = (): Observable<AccessDataActionTypes> => {
    return keycloakService.token()
        .pipe(
            map(token => jwtDecode(token)),
            map((jwt) => (jwt as JwtToken).resource_access.viewer.roles),
            map(roles => ({development: IS_DEV, permissions: roles} as AccessDTO)),
            map((accessDto) => viewerApiServiceReceivedAccessData(accessDto)),
            catchError(() => of(viewerApiServiceReceivingAccessDataFailed()))
        );
};

export const fetchSearchOptions = (
    source: string,
    metadata: VersionMetadata,
    searchValue: string,
): Observable<ViewerApiServiceSearchOptions> => {
    const searchOptionsPromise = viewerApiClientV2().then((api) =>
        api.searchOptionsController.getSearchOptions(source, metadata.version, searchValue));

    return from(searchOptionsPromise).pipe(
        map((searchOptions: SearchOptionDTO[]) => {
            const searchFeatures: SearchFeature[] = searchOptions.map((searchOption: SearchOptionDTO) => ({
                id: searchOption.id,
                displayValue: searchOption.displayValue,
                coordinates: {
                    latitude: searchOption.coordinates.latitude,
                    longitude: searchOption.coordinates.longitude,
                },
            }));
            return viewerApiServiceSearchOptionsCompleted(searchFeatures);
        }),
        catchError(() => of(viewerApiServiceSearchOptionsFailedWithUnexpectedError())),
    );
};

export const loadPrefixesV2 = (prefixType: keyof PrefixFilterOptions): Observable<ViewerApiServiceLoadPrefixData> => {
    let getPrefixes: Promise<string[]>;
    if (prefixType === 'avg') {
        getPrefixes = viewerApiClientV2().then((api) => api.avgProxyController.getPrefixes1());
    } else {
        getPrefixes = viewerApiClientV2().then((api) => api.travelTimeProxyController.getPrefixes());
    }

    return from(getPrefixes).pipe(
        map((response) => {
            return viewApiServiceLoadPrefixV2DataCompleted(prefixType, response);
        }),
        catchError(() => of(viewerApiServiceLoadPrefixDataFailedWithUnexpectedError())),
    );
};

export const loadMetadata = (dataSourceId: DataSourceId): Observable<SourceDataDataActionTypes> => {
    const getMetadata = dataStreamerApi().then((api) => api.metadataController.getMetadataById(dataSourceId));

    return from(getMetadata).pipe(
        map((metadata) => {
            return handleMetadataReceivedForDataSource(dataSourceId, metadata);
        }),
        catchError(() => of(noMetadataHandlerForDataSourceFound())),
    );
};
